import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import UserMenu from './UserMenu';

import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [userRole, setUserRole] = useState('partner');
  const [notifications, setNotifications] = useState(3);
  const [language, setLanguage] = useState('fr');
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const location = useLocation();

  // Enhanced navigation items with better organization
  const navigationItems = [
  {
    label: 'Tableau de bord',
    path: '/partner-dashboard',
    icon: 'LayoutDashboard',
    requiredRole: ['partner', 'admin', 'agent', 'artist', 'venue'],
    tooltip: 'Vue d\'ensemble des performances et métriques'
  },
  {
    label: 'Bookings',
    path: '/booking-dashboard',
    icon: 'Calendar',
    requiredRole: ['partner','admin','agent','artist','venue'],
    tooltip: 'Gérer et créer des bookings (MVP)'
  },
  {
    label: 'Artistes',
    path: '/artists',
    icon: 'Users',
    requiredRole: ['partner','admin','agent','artist','venue','media'],
    tooltip: 'Annuaire public des artistes'
  },
  {
    label: 'Recherche',
    path: '/advanced-search',
    icon: 'Search',
    requiredRole: ['partner', 'admin', 'agent', 'artist', 'venue'],
    tooltip: 'Rechercher des artistes, agents ou venues'
  },
  {
    label: 'Messagerie',
    path: '/private-messaging',
    icon: 'MessageCircle',
    requiredRole: ['partner', 'admin', 'agent', 'artist', 'venue'],
    tooltip: 'Messages privés et communications'
  },
  {
    label: 'Commissions',
    path: '/commission-tracking',
    icon: 'DollarSign',
    requiredRole: ['partner', 'admin', 'agent'],
    tooltip: 'Suivi des revenus et paiements'
  },
  {
    label: 'Profil',
    path: userRole === 'artist' ? '/enhanced-artist-profile' : '/partner-profile',
    icon: 'User',
    requiredRole: ['partner', 'admin', 'agent', 'artist', 'venue'],
    tooltip: 'Gestion du profil et paramètres'
  }];


  const filteredNavItems = navigationItems?.filter((item) =>
  item?.requiredRole?.includes(userRole)
  );

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // Close other menus when opening mobile menu
    setIsUserMenuOpen(false);
    setIsProfileDropdownOpen(false);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
    // Close other menus when opening user menu
    setIsMobileMenuOpen(false);
    setIsProfileDropdownOpen(false);
  };

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
    // Close other menus when opening profile dropdown
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  };

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  const { signOut, isAuthenticated, role, signingOut } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    // Fermer les menus et déclencher la déconnexion
    setIsProfileDropdownOpen(false);
    setIsUserMenuOpen(false);
    await signOut();
    // Navigation de secours si on reste sur une route protégée
    if (['/partner-dashboard','/commission-tracking','/partner-profile','/private-messaging','/admin-dashboard','/admin-roadmap'].includes(location?.pathname)) {
      navigate('/', { replace: true });
    }
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setLanguage(savedLanguage);
    }

    const savedRole = localStorage.getItem('userRole');
    if (savedRole) setUserRole(savedRole);
  }, []);

  // Sync role from context
  useEffect(() => {
    if (role && role !== userRole) setUserRole(role)
  }, [role]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isProfileDropdownOpen && !event?.target?.closest('.profile-dropdown')) {
        setIsProfileDropdownOpen(false);
      }
      if (isUserMenuOpen && !event?.target?.closest('.user-menu-container')) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileDropdownOpen, isUserMenuOpen]);

  // Get unread message count (mock data)
  const getUnreadMessages = () => {
    // This would come from your messaging system
    return 2;
  };

  return (
  <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border shadow-sm">
      <div className="items-center justify-between h-16 px-6 flex bg-black">
        {/* Logo */}
  <Link to={isAuthenticated ? "/partner-dashboard" : "/"} className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-8 h-8 bg-accent rounded-lg">
            <Icon name="Music" size={20} color="white" />
          </div>
          <span className="text-xl font-semibold text-foreground">Artis.io</span>
        </Link>

        {/* Desktop Navigation - Show only key items */}
  {isAuthenticated && (
  <nav className="hidden lg:flex items-center space-x-6">
          {filteredNavItems?.slice(0, 4)?.map((item) =>
          <Link
            key={item?.path}
            to={item?.path}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 relative ${
            location?.pathname === item?.path ?
            'text-accent bg-accent/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`
            }
            title={item?.tooltip}>

              <Icon name={item?.icon} size={16} />
              <span>{item?.label}</span>
              {/* Show unread count for messaging */}
              {item?.path === '/private-messaging' && getUnreadMessages() > 0 &&
            <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {getUnreadMessages()}
                </span>
            }
            </Link>
          )}
  </nav>) }

        {/* Right Side Actions */}
  <div className="flex items-center space-x-3">
          {/* Quick Access: Search (Desktop only) */}
          {isAuthenticated && (<div className="hidden lg:block">
            <Link to="/advanced-search">
              <Button variant="ghost" size="icon" title="Recherche rapide">
                <Icon name="Search" size={18} />
              </Button>
            </Link>
          </div>)}

          {/* Quick Access: Messages (Desktop only) */}
          {isAuthenticated && (<div className="hidden lg:block relative">
            <Link to="/private-messaging">
              <Button variant="ghost" size="icon" className="relative" title="Messages">
                <Icon name="MessageCircle" size={18} />
                {getUnreadMessages() > 0 &&
                <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-xs rounded-full h-4 w-4 flex items-center justify-center">
                    {getUnreadMessages()}
                  </span>
                }
              </Button>
            </Link>
          </div>)}

          {/* User Menu Toggle (Desktop) */}
          {isAuthenticated && (<div className="hidden md:block relative user-menu-container">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleUserMenu}
              className="flex items-center space-x-2"
              title="Menu utilisateur">

              <Icon name="Grid3X3" size={16} />
              <span className="hidden lg:inline">Menu</span>
            </Button>

            {/* User Menu Dropdown */}
            {isUserMenuOpen &&
            <div className="absolute right-0 mt-2 w-80 z-50 bg-card rounded-lg border border-border/80 shadow-xl">
                <UserMenu
                userRole={userRole}
                isOpen={isUserMenuOpen}
                onToggle={toggleUserMenu} />

              </div>
            }
          </div>)}

          {/* Language Selector */}
          {isAuthenticated && (<div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleLanguageChange(language === 'fr' ? 'en' : 'fr')}
              className="hidden md:flex"
              title="Changer la langue">

              <Icon name="Globe" size={16} className="mr-1" />
              {language?.toUpperCase()}
            </Button>
          </div>)}

          {/* Notifications */}
          {isAuthenticated && (<div className="relative">
            <Button variant="ghost" size="icon" className="relative" title="Notifications">
              <Icon name="Bell" size={18} />
              {notifications > 0 &&
              <span className="absolute -top-1 -right-1 bg-error text-error-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {notifications}
                </span>
              }
            </Button>
          </div>)}

          {/* User Profile Dropdown */}
          {isAuthenticated && (<div className="relative profile-dropdown">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleProfileDropdown}
              className="flex items-center space-x-2"
              title="Profil utilisateur">

              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                <Icon name="User" size={16} color="white" />
              </div>
              <Icon name="ChevronDown" size={14} className="bg-black" />
            </Button>

            {isProfileDropdownOpen &&
            <div className="absolute right-0 mt-2 w-56 bg-card border border-border/80 rounded-md shadow-xl z-50">
                <div className="py-2">
                  {/* User Info */}
                  <div className="px-4 py-2 border-b border-border/80 bg-muted">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                        <Icon name="User" size={18} color="white" />
                      </div>
                      <div>
                        <div className="font-medium text-sm text-popover-foreground">Alexandre Dubois</div>
                        <div className="text-xs text-muted-foreground capitalize">{userRole}</div>
                      </div>
                    </div>
                  </div>

                  {/* Profile Links */}
                  <div className="py-1 bg-card">
                    <Link
                    to={userRole === 'artist' ? '/enhanced-artist-profile' : '/partner-profile'}
                    className="flex items-center px-4 py-2 text-sm text-popover-foreground hover:bg-muted"
                    onClick={() => setIsProfileDropdownOpen(false)}>

                      <Icon name="User" size={16} className="mr-3" />
                      Mon Profil
                    </Link>
                    <Link
                    to="/help-documentation"
                    className="flex items-center px-4 py-2 text-sm text-popover-foreground hover:bg-muted"
                    onClick={() => setIsProfileDropdownOpen(false)}>

                      <Icon name="HelpCircle" size={16} className="mr-3" />
                      Aide & Support
                    </Link>
                    <div className="border-t border-border my-1"></div>
                    <button
                    onClick={handleLogout}
                    disabled={signingOut}
                    className="flex items-center w-full px-4 py-2 text-sm text-popover-foreground hover:bg-muted disabled:opacity-60">

                      <Icon name="LogOut" size={16} className="mr-3" />
                      {signingOut ? 'Déconnexion...' : 'Déconnexion'}
                    </button>
                  </div>
                </div>
              </div>
            }
          </div>)}

          {/* Mobile Menu Button */}
          {isAuthenticated && (
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMobileMenu}
              className="md:hidden"
              title="Menu mobile">
              <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
            </Button>
          )}
        </div>
      </div>

      {/* Mobile Menu Overlay */}
    {isMobileMenuOpen &&
    <div className="fixed inset-0 z-40 md:hidden">
      <div className="fixed inset-0 bg-background/70" onClick={toggleMobileMenu}></div>
      <div className="fixed top-16 left-0 right-0 bg-card border-b border-border/80 shadow-xl max-h-[calc(100vh-4rem)] overflow-y-auto">
            {/* Mobile User Menu */}
            <div className="px-4 py-4 bg-card">
              <UserMenu
              userRole={userRole}
              isOpen={true}
              onToggle={toggleMobileMenu}
              className="border-0 shadow-none bg-surface" />

            </div>
            
            <div className="border-t border-border/80 px-6 py-4 bg-card">
              <button
              onClick={() => {
                handleLanguageChange(language === 'fr' ? 'en' : 'fr');
                toggleMobileMenu();
              }}
              className="flex items-center space-x-3 px-4 py-3 rounded-md text-base font-medium text-muted-foreground hover:text-foreground hover:bg-muted w-full">

                <Icon name="Globe" size={20} />
                <span>Langue: {language?.toUpperCase()}</span>
              </button>
            </div>
          </div>
        </div>
      }
    </header>);

};

export default Header;